import React, { useState, useEffect } from 'react'
import { useTranslation } from 'next-i18next/pages'
import ReviewComment from './ReviewComment/ReviewComment'
import s from './ReviewComments.module.scss'
import type { ProductReviews } from '@pylot-data/pylotschema'
import CommentPaginator from './CommentPaginator/CommentPaginator'
import { useAuthUI, useUser } from '@corratech/pylot-auth-manager'
import { ReviewForm } from '../ReviewForm'
import { translateRatingToDecimal } from '@lib/utils/translateRatingToDecimal'
import Star from '@components/icons/Star'
import { useIsMobileScreen } from '@lib/utils/mediaQuery'

type ReviewCommentsProps = {
    reviews?: ProductReviews
    simplifiedComments?: boolean
    sku: string
    ratingSummary?: number
}

const ReviewComments: React.FC<ReviewCommentsProps> = ({
    reviews,
    simplifiedComments = false,
    sku,
    ratingSummary
}) => {
    const { t } = useTranslation(['pdp'])
    const MAX_PER_PAGE = simplifiedComments ? 3 : 5

    const [currentPage, setCurrentPage] = useState(1)
    const [enableReviewForm, setEnableReviewForm] = useState(false)
    const { customer, isSignedIn, loading: authLoading } = useUser()
    const { openAuthModal } = useAuthUI()
    const isMobile = useIsMobileScreen()

    const comments = reviews
        ? reviews?.items?.map((comment: any, index: number) => {
              return <ReviewComment key={index} comment={comment} />
          })
        : []
    const totalPages = Math.ceil(comments.length / MAX_PER_PAGE)
    const hasNoComments = comments.length == 0

    const visibleComponents = comments.slice(
        (currentPage - 1) * MAX_PER_PAGE,
        currentPage * MAX_PER_PAGE
    )

    useEffect(() => {
        if (hasNoComments) {
            setEnableReviewForm(true)
        } else {
            setEnableReviewForm(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasNoComments])

    const noReviewsContainer = hasNoComments && (
        <div>{t('pdp||No comments for this product yet.')}</div>
    )

    const enableWriteReviewsButton = !enableReviewForm && (
        <button
            className={s['comments-form-heading']}
            onClick={() => setEnableReviewForm((prevstate) => !prevstate)}
        >
            {t('pdp||Write a comment')}
        </button>
    )

    const shouldBeLoginText = !isSignedIn ? (
        <div className="text-md">
            {t('pdp||You must sign in to rate the product.')}
            {!isSignedIn && (
                <>
                    <span className="text-md font-medium">
                        <button
                            className="underline cursor-pointer"
                            onClick={openAuthModal}
                        >
                            {t('pdp||Log in here')}
                        </button>
                    </span>
                    <br />
                    <br />
                </>
            )}
        </div>
    ) : null

    const handleCloseForm = () => {
        setEnableReviewForm(false)
    }

    const shouldShowPaginator = comments.length >= MAX_PER_PAGE

    return (
        <div className={s['comments-container']}>
            <div className={s['comments-title']}>
                <h4 className={s['comments-title-heading']}>
                    {t('pdp||Comments')}
                </h4>
                {!isMobile && enableWriteReviewsButton}
            </div>
            {simplifiedComments && (
                <div className={s['reviews-title-mobile']}>
                    <div className={s['reviews-subtitle-mobile']}>
                        <h4>{t('pdp||Comments')}</h4>
                        {enableWriteReviewsButton}
                    </div>
                    {!enableReviewForm && (
                        <div className={s['reviews-comments-star']}>
                            {translateRatingToDecimal(ratingSummary)}
                            <Star />
                        </div>
                    )}
                </div>
            )}
            {!enableReviewForm ? (
                <>
                    {noReviewsContainer}
                    {visibleComponents.map((component, index) => (
                        <div key={index}>{component}</div>
                    ))}
                    {!simplifiedComments && shouldShowPaginator && (
                        <CommentPaginator
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            totalPages={totalPages}
                        />
                    )}
                </>
            ) : (
                <ReviewForm
                    sku={sku}
                    customer={customer}
                    handleCloseForm={handleCloseForm}
                    isSignedIn={isSignedIn}
                    authLoading={authLoading}
                />
            )}
        </div>
    )
}

export default ReviewComments
